import React from 'react';

import { v2Image } from './images';

/**
 * Card art is cover-cropped, and every card box is wider than the tall handoff
 * assets once the viewport narrows, so the crop eats the top and bottom of the
 * photo. People photography carries its subject in the upper third, which a
 * centred crop drops first: on mobile the search spotlight keeps the torso and
 * cuts the head off at the neck. Bias the crop to the top of the frame unless
 * the asset below names a focal point of its own.
 */
export const PHOTO_FOCAL_DEFAULT = 'center top';

/**
 * Where the subject sits in each handoff asset, as an `object-position` value.
 * The vertical percentage is the height of the face (or of the still-life
 * subject) in the source photo; CSS renders that point at the same percentage
 * of the card, which keeps it in frame at every crop ratio. Keys are the
 * handoff `.png` names used across the catalog, matching {@link v2Image}.
 */
const PHOTO_FOCAL: Record<string, string> = {
  // People: the percentage is the face.
  'clinician-portrait.png': 'center top',
  'coll-energy-iron__core-360-free-01.png': 'center 35%',
  'coll-immunity.png': 'center 35%',
  'coll-mens-health.png': 'center 25%',
  'coll-sexual-health__sexual-health-free-01.png': 'center 30%',
  'coll-womens-health__core-360-paid-06.png': 'center 40%',
  'lab-visit-female.png': 'center 30%',
  'lab-visit-male.png': 'center 25%',
  'lab-visit-neutral.png': 'center 40%',
  'new-apob.png': 'center 30%',
  'new-ferritin__core-360-paid-01.png': 'center 30%',
  'new-insulin__core-360-paid-04.png': 'center 40%',
  'new-testosterone__core-360-paid-05.png': 'center 35%',
  'new-tsh__core-360-paid-02.png': 'center 35%',
  'spot-heart__core-360-free-05-web.png': 'center 30%',
  'spot-longevity__heart-metabolic-free-01.png': 'center 65%',
  'spot-sleep.png': 'center 30%',
  'spot-sugar__core-360-paid-04.png': 'center 50%',
  'spot-testosterone.png': 'center 30%',
  'spot-thyroid__core-360-free-01.png': 'center 35%',
  // Still life: the percentage is the object the shot is built around, which
  // sits at or below the middle of the frame in every one of these.
  'spot-allergy__skin-allergy-paid-02.png': 'center 50%',
  'spot-blood.png': 'center 55%',
  'spot-immunity.png': 'center 50%',
  'spot-inflammation.png': 'center 55%',
  'spot-kidney.png': 'center 50%',
  'spot-liver.png': 'center 50%',
  'spot-neutral__hormone-free-01.png': 'center 45%',
  'spot-sti__sexual-health-free-02.png': 'center 55%',
  'spot-urinary.png': 'center 50%',
  'spot-vitamins__immune-inflammation-free-02.png': 'center 55%',
};

/** The `object-position` a cover-cropped card should use for `image`. */
export const photoFocal = (image: string): string =>
  PHOTO_FOCAL[image] ||
  PHOTO_FOCAL[image.replace(/\.jpg$/, '.png')] ||
  PHOTO_FOCAL_DEFAULT;

type CardPhotoProps = {
  /** Catalog image name, e.g. `spot-heart__core-360-free-05-web.png`. */
  image: string;
  alt?: string;
  loading?: 'lazy' | 'eager';
};

/**
 * The cover-cropped photo every Everywhere card renders: the surrounding
 * element sets the box and `object-fit`, this sets the source and the focal
 * point. Renders nothing when the asset is missing so a card that lost its art
 * keeps its placeholder background instead of a broken image.
 */
export function CardPhoto({
  image,
  alt = '',
  loading = 'lazy',
}: CardPhotoProps): JSX.Element | null {
  const src = v2Image(image);
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      style={{ objectPosition: photoFocal(image) }}
    />
  );
}

export default CardPhoto;
